/* global */

(function () {
  'use strict';

  if (window.customElements.get('ugc-videos')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class UgcVideos extends HTMLElement {
    constructor() {
      super();
      this.cards = [];
      this.modal = null;
      this.modalVideo = null;
      this.modalMeta = null;
      this.currentIndex = 0;
      this.lastFocused = null;
      this.io = null;
      this.touchStartY = 0;
      this.touchStartX = 0;
      this.boundKeyHandler = null;
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.js-ugc-video-open'));
      if (this.cards.length === 0) return;

      this.modal = this.querySelector('[data-ugc-modal]');
      this.modalVideo = this.querySelector('[data-ugc-modal-video]');
      this.modalMeta = this.querySelector('[data-ugc-modal-meta]');

      this.bindCardClicks();
      this.bindModalControls();
      this.setupCardAutoplay();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.boundKeyHandler) document.removeEventListener('keydown', this.boundKeyHandler);
    }

    /* ----------------------------- Card autoplay ----------------------------- */

    setupCardAutoplay() {
      const autoplayEnabled = this.dataset.autoplayInCard === 'true' && !REDUCED_MOTION;
      if (!autoplayEnabled) return;
      if (!('IntersectionObserver' in window)) return;

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('.ugc-video__video');
          if (!video) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.activateCardVideo(video);
          } else {
            this.deactivateCardVideo(video);
          }
        });
      }, { threshold: [0, 0.5, 1], rootMargin: '200px 0px' });

      this.cards.forEach((card) => this.io.observe(card));
    }

    activateCardVideo(video) {
      if (!video.src) {
        const src = video.dataset.videoCardSrc;
        if (!src) return;
        video.src = src;
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked: leave poster visible */ });
      }
      video.parentElement && video.parentElement.classList.add('ugc-video__card--playing');
    }

    deactivateCardVideo(video) {
      try { video.pause(); } catch (e) { /* noop */ }
      video.parentElement && video.parentElement.classList.remove('ugc-video__card--playing');
    }

    /* ------------------------------- Modal ------------------------------- */

    bindCardClicks() {
      this.cards.forEach((card) => {
        card.addEventListener('click', (evt) => {
          evt.preventDefault();
          const video = card.querySelector('.ugc-video__video');
          if (!video) return;
          if (!video.src && video.dataset.videoCardSrc) {
            video.src = video.dataset.videoCardSrc;
          }
          if (video.paused) {
            const p = video.play();
            if (p && typeof p.catch === 'function') p.catch(() => { /* noop */ });
            card.classList.add('ugc-video__card--playing');
          } else {
            try { video.pause(); } catch (e) { /* noop */ }
            card.classList.remove('ugc-video__card--playing');
          }
        });
      });
    }

    bindModalControls() {
      if (!this.modal) return;

      this.modal.querySelectorAll('[data-ugc-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeModal());
      });

      const prevBtn = this.modal.querySelector('[data-ugc-modal-prev]');
      const nextBtn = this.modal.querySelector('[data-ugc-modal-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.navigate(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.navigate(1));

      // Touch / swipe (vertical preferred, horizontal fallback)
      this.modal.addEventListener('touchstart', (e) => {
        if (!e.touches[0]) return;
        this.touchStartY = e.touches[0].clientY;
        this.touchStartX = e.touches[0].clientX;
      }, { passive: true });

      this.modal.addEventListener('touchend', (e) => {
        if (!e.changedTouches[0]) return;
        const dy = e.changedTouches[0].clientY - this.touchStartY;
        const dx = e.changedTouches[0].clientX - this.touchStartX;
        if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) {
          this.navigate(dy < 0 ? 1 : -1);
        } else if (Math.abs(dx) > 60) {
          this.navigate(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
    }

    openModal(index, opener) {
      if (!this.modal) return;
      this.currentIndex = index;
      this.lastFocused = opener || document.activeElement;

      this.loadModalVideo(index);
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      // Pause any in-card videos to avoid double audio
      this.querySelectorAll('.ugc-video__video').forEach((v) => { try { v.pause(); } catch (e) { /* noop */ } });

      this.boundKeyHandler = (e) => this.handleKey(e);
      document.addEventListener('keydown', this.boundKeyHandler);

      const closeBtn = this.modal.querySelector('[data-ugc-modal-close]');
      if (closeBtn) {
        // Defer focus so the modal is paint-ready
        requestAnimationFrame(() => closeBtn.focus());
      }
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';

      try { this.modalVideo.pause(); } catch (e) { /* noop */ }
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load();

      if (this.boundKeyHandler) {
        document.removeEventListener('keydown', this.boundKeyHandler);
        this.boundKeyHandler = null;
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }

      // Resume in-card autoplay
      if (this.io) {
        this.cards.forEach((card) => this.io.unobserve(card));
        this.cards.forEach((card) => this.io.observe(card));
      }
    }

    navigate(delta) {
      const next = this.currentIndex + delta;
      if (next < 0 || next >= this.cards.length) return;
      this.currentIndex = next;
      this.loadModalVideo(next);
    }

    loadModalVideo(index) {
      const card = this.cards[index];
      if (!card) return;

      const src = card.dataset.videoSrc;
      const handle = card.dataset.videoHandle;
      const platform = card.dataset.videoPlatform;
      const cta = card.dataset.videoCta;

      if (src) {
        this.modalVideo.src = src;
        this.modalVideo.load();
        const playPromise = this.modalVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { /* user must tap play */ });
        }
      }

      // Update meta overlay
      let metaHtml = '';
      if (handle) {
        metaHtml += `<span class="ugc-videos__modal-handle">${this.escapeHtml(handle)}</span>`;
      }
      if (cta) {
        metaHtml += `<a class="ugc-videos__modal-cta" href="${this.escapeHtml(cta)}" target="_blank" rel="noopener">View profile</a>`;
      }
      if (this.modalMeta) {
        this.modalMeta.innerHTML = metaHtml;
        this.modalMeta.dataset.platform = platform || 'none';
      }

      // Update prev/next button visibility
      const prevBtn = this.modal.querySelector('[data-ugc-modal-prev]');
      const nextBtn = this.modal.querySelector('[data-ugc-modal-next]');
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === this.cards.length - 1;
    }

    handleKey(e) {
      if (e.key === 'Escape') {
        this.closeModal();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        this.navigate(-1);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        this.navigate(1);
      }
    }

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }
  }

  window.customElements.define('ugc-videos', UgcVideos);
})();
